import React, { useMemo, useRef, useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import type { GroupBase, SingleValue } from 'react-select';
import { useTheme } from '@mui/material/styles';
import '../../../components/InputEdit/SearchArea/SearchArea.css';

/* =========================================================
 * 型
 * ======================================================= */

export type Option = {
  value: string;
  label: string;
  groupLabel?: string;
  isDisabled?: boolean;
};

export type CommonSelectProps = {
  id: string;
  placeholder?: string;
  options: Option[];
  /** 選択中（未選択は null）
   * CommonSelect は「完全 controlled」にする。
   * 内部では state を一切持たず、親から渡された value が唯一のソース・オブ・トゥルース。
   * 親が null や '' を渡したら、その瞬間に UI も未選択表示になる。
   */
  value: Option | null;
  /** 統一コールバック：選択肢 or null */
  onChange?: (opt: Option | null) => void;
  /** 必要なら string も同時に通知（任意） */
  onChangeString?: (value: string, label: string) => void;

  isClearable?: boolean;
  isSearchable?: boolean;
  /** コンポーネントを無効化（react-select の isDisabled を委譲） */
  isDisabled?: boolean;
  noOptionsMessage?: string;
  className?: string;
  /** z-index 衝突回避のために portal 化（未指定時は document.body） */
  menuPortalTarget?: HTMLElement | null;
  /** メニューの展開方向（既定は "auto" で自動判定） */
  menuPlacement?: 'auto' | 'top' | 'bottom';
  /** "新規作成" を常に無効化（既定で false） */
  allowCreate?: boolean;
  /** Keep and commit text that is not in options. */
  allowFreeText?: boolean;
  /** Commit the current search text when the control loses focus. */
  commitInputOnBlur?: boolean;
  /** Option-specific secondary text shown in the dropdown menu. */
  optionDescriptions?: Record<
    string,
    { summaryText: string; tooltipText: string }
  >;
  /** Used when an option has no explicit description (for example, public by default). */
  defaultOptionDescription?: { summaryText: string; tooltipText: string };
};

/* =========================================================
 * ユーティリティ
 * ======================================================= */

const norm = (s: any) => String(s ?? '').normalize('NFKC').trim();
const isPastTeamGroupLabel = (label: string) => norm(label) === '過去チーム';

const uniqueOptions = (opts: Option[]) => {
  const seen = new Set<string>();
  return (opts ?? []).filter((o) => {
    const k = `${o.value}__${o.label}__${norm(o.groupLabel ?? '')}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/* =========================================================
 * コンポーネント
 * ======================================================= */

const CommonSelect: React.FC<CommonSelectProps> = (props) => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const {
    id,
    placeholder,
    options,
    value,
    onChange,
    onChangeString, // 後方互換・任意
    isClearable = true,
    isSearchable = true,
    isDisabled = false,
    noOptionsMessage = '該当する候補がありません',
    className,
    menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
    menuPlacement = 'auto',
    allowCreate = false,
    allowFreeText = false,
    commitInputOnBlur = false,
    optionDescriptions,
    defaultOptionDescription,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const skipNextBlurCommitRef = useRef(false);

  // options 去重
  const baseOpts = useMemo(() => uniqueOptions(options || []), [options]);
  // 親がチーム変更で value を '' / null にしたら、
  // こちらも素直に未選択にしてほしいので、
  // options はそのまま baseOpts を使い、
  // 古い値を勝手に注入しないようにします。
  const opts = useMemo(() => {
    return baseOpts;
  }, [baseOpts]);

  const groupedOptions = useMemo(() => {
    const hasGroups = opts.some((o) => norm(o.groupLabel ?? '') !== '');
    if (!hasGroups) return opts as Array<Option | GroupBase<Option>>;

    const ungrouped: Option[] = [];
    const orderedGroups: Array<{ label: string; options: Option[] }> = [];
    const groupIndex = new Map<string, number>();

    opts.forEach((opt) => {
      const key = norm(opt.groupLabel ?? '');
      if (!key) {
        ungrouped.push(opt);
        return;
      }
      if (!groupIndex.has(key)) {
        groupIndex.set(key, orderedGroups.length);
        orderedGroups.push({ label: key, options: [] });
      }
      orderedGroups[groupIndex.get(key) as number].options.push(opt);
    });

    orderedGroups.sort((a, b) => {
      const aPast = isPastTeamGroupLabel(a.label);
      const bPast = isPastTeamGroupLabel(b.label);
      if (aPast === bPast) return 0;
      return aPast ? 1 : -1;
    });

    return [...ungrouped, ...orderedGroups] as Array<Option | GroupBase<Option>>;
  }, [opts]);

  // react-select に渡す現在値（null 可）
  const currentValue = useMemo(() => {
    if (!value || (!value.value && !value.label)) {
      // 親が null / '' を渡したとき:
      // → ここも null を返すことで、react-select は placeholder 表示になる
      return null;
    }

    // 親から渡ってきた value が今の候補(opts)に存在するかだけを見る
    const hit =
      opts.find((o) => norm(o.value) === norm(value.value)) ||
      opts.find((o) => norm(o.label) === norm(value.label));

    // 候補にいなければ null を返して未選択扱いにする。
    // （古いチームの承認者などを残さない）
    return hit || (allowFreeText ? value : null);
  }, [allowFreeText, opts, value]);

  const handleChange = (sel: SingleValue<Option>) => {
    skipNextBlurCommitRef.current = true;
    setInputValue('');

    //    CommonSelect 自身は state を持たず、
    //    親に「今こうなった」と伝えるだけ。
    //    親がその値を保持し直して、また props.value として戻してくる。
    onChange?.(sel ?? null);

    // 後方互換（任意で string も欲しい場合）
    if (!sel) {
      onChangeString?.('', '');
    } else {
      onChangeString?.(sel.value, sel.label);
    }
  };

  const commitFreeText = (raw: string) => {
    if (!allowFreeText) return;
    const text = norm(raw);
    if (!text) return;

    const opt: Option = { value: text, label: text };
    onChange?.(opt);
    onChangeString?.(opt.value, opt.label);
  };

  const handleInputChange = (nextValue: string, meta: { action: string }) => {
    if (!allowFreeText && !commitInputOnBlur) return nextValue;

    if (meta.action === 'input-change') {
      skipNextBlurCommitRef.current = false;
      setInputValue(nextValue);
    } else if (meta.action === 'set-value') {
      setInputValue('');
    } else if (meta.action === 'input-blur' && !commitInputOnBlur) {
      setInputValue('');
    }

    return nextValue;
  };

  const handleBlur = () => {
    if (!commitInputOnBlur) {
      if (inputValue) setInputValue('');
      return;
    }

    if (skipNextBlurCommitRef.current) {
      skipNextBlurCommitRef.current = false;
      setInputValue('');
      return;
    }

    commitFreeText(inputValue);
    setInputValue('');
  };

  return (
    <div className={className}>
      <CreatableSelect<Option, false, GroupBase<Option>>
        inputId={id}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        options={groupedOptions}
        formatOptionLabel={(option, meta) => {
          const optionValue = norm(option.value);
          const description = optionValue
            ? optionDescriptions?.[optionValue] ?? defaultOptionDescription
            : undefined;
          if (!description) return option.label;

          if (meta.context !== 'menu') {
            return <span title={description.tooltipText}>{option.label}</span>;
          }

          return (
            <span
              title={description.tooltipText}
              style={{
                minWidth: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <span style={{ minWidth: 0 }}>{option.label}</span>
              <span
                style={{
                  minWidth: 0,
                  maxWidth: '58%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: isDark ? '#94a3b8' : '#64748b',
                  fontSize: 11,
                  textAlign: 'right',
                }}
              >
                {description.summaryText}
              </span>
            </span>
          );
        }}
        isOptionDisabled={(opt: Option) => Boolean(opt?.isDisabled)}
        value={currentValue}
        inputValue={allowFreeText || commitInputOnBlur ? inputValue : undefined}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder || ''}
        // 「自由入力」をしない（allowCreate=false が既定）
        isValidNewOption={(input) => !!allowCreate && norm(input).length > 0}
        formatCreateLabel={(input) => (allowCreate ? `「${input}」を追加` : null)}
        onCreateOption={(label) => {
          if (!allowCreate) return;
          skipNextBlurCommitRef.current = true;
          setInputValue('');
          const newOpt: Option = { value: norm(label), label: norm(label) };
          onChange?.(newOpt);
          onChangeString?.(newOpt.value, newOpt.label);
        }}
        menuPortalTarget={menuPortalTarget}
        menuPlacement={menuPlacement}
        styles={{
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
          control: (base: any, state: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && {
              backgroundColor: '#1e293b',
              borderColor: state.isFocused ? '#64748b' : '#334155',
              boxShadow: state.isFocused ? '0 0 0 1px #64748b' : 'none',
              '&:hover': { borderColor: '#475569' },
            }),
          }),
          valueContainer: (base: any) => ({ ...base, textAlign: 'left' }),
          singleValue: (base: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && { color: '#e2e8f0' }),
          }),
          placeholder: (base: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && { color: '#64748b' }),
          }),
          input: (base: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && { color: '#e2e8f0' }),
          }),
          menu: (base: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && { backgroundColor: '#1e293b', borderColor: '#334155' }),
          }),
          menuList: (base: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && { backgroundColor: '#1e293b' }),
          }),
          option: (base: any, state: any) => ({
            ...base,
            textAlign: 'left',
            ...(isDark && {
              backgroundColor: state.isSelected
                ? '#334155'
                : state.isFocused
                ? '#243044'
                : '#1e293b',
              color: '#e2e8f0',
              '&:active': { backgroundColor: '#334155' },
            }),
          }),
          groupHeading: (base: any) => ({
            ...base,
            minHeight: 27,
            height: 27,
            display: 'flex',
            alignItems: 'center',
            padding: '0 12px',
            margin: 0,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1,
            color: '#fff',
            backgroundColor: isDark ? '#475569' : '#9e9e9e',
          }),
          clearIndicator: (base: any) => ({
            ...base,
            ...(isDark && { color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }),
          }),
          dropdownIndicator: (base: any) => ({
            ...base,
            ...(isDark && { color: '#94a3b8', '&:hover': { color: '#e2e8f0' } }),
          }),
          indicatorSeparator: (base: any) => ({
            ...base,
            ...(isDark && { backgroundColor: '#334155' }),
          }),
        }}
        noOptionsMessage={() => noOptionsMessage}
      />
    </div>
  );
};

export default CommonSelect;
